import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpTrayIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import quoteService, { type SalesforceImportResult } from '../../services/quote.service';

/**
 * Import Salesforce Customers (V1 salesforce_quote.pl importsalesforce). Pick a
 * CSV exported from Salesforce (10 required columns) and upload it; the server
 * upserts quote.salesforce and returns the run counts.
 */
const SalesforceImportPage: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SalesforceImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onImport = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await quoteService.importSalesforce(file));
    } catch (e: unknown) {
      // Surface the backend's 422 (missing column) / dev-gate / 500 detail.
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-[18px] max-w-[640px]">
      <button
        type="button"
        onClick={() => navigate('/order-management/quotes')}
        className="inline-flex items-center gap-1 text-[0.75rem] font-medium text-blue-600 hover:underline mb-3"
      >
        <ArrowLeftIcon className="h-3.5 w-3.5" /> Back to Quotes
      </button>

      <section className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
          <ArrowUpTrayIcon className="h-4 w-4 text-blue-600" />
          <h2 className="m-0 text-[0.8125rem] font-bold text-slate-900 dark:text-gray-100">Import Salesforce Customers</h2>
        </div>

        <div className="px-4 py-4 flex flex-col gap-3">
          <p className="text-[0.75rem] text-slate-500 dark:text-gray-400 m-0">
            Upload a CSV exported from Salesforce. Required columns: Id, IsDeleted, Name,
            BillingStreet, BillingCity, BillingState, BillingPostalCode, BillingCountry, Phone, Fax.
          </p>

          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              aria-label="Salesforce CSV file"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setError(null); }}
              className="text-[0.75rem] text-slate-700 dark:text-gray-200"
            />
          </div>

          <div>
            <button
              type="button"
              onClick={onImport}
              disabled={!file || busy}
              className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded disabled:opacity-40 hover:bg-blue-700"
            >
              {busy ? 'Importing…' : 'Import'}
            </button>
          </div>

          {error && (
            <div className="px-3 py-2 text-[0.75rem] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
              {error}
            </div>
          )}

          {result && (
            <div className="px-3 py-2.5 text-[0.75rem] text-slate-700 dark:text-gray-200 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded">
              <div className="font-semibold mb-1 text-slate-900 dark:text-gray-100">Import complete</div>
              <div>Rows read: <b>{result.raw}</b></div>
              <div>Created: <b>{result.created}</b> · Updated: <b>{result.updated}</b> · No change: <b>{result.unchanged}</b></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SalesforceImportPage;
